import type { TraderSearchResult } from '../../api/traderSearch';
import { formatPnlShort } from '../../utils/traderFormat';
import { EM_DASH, formatDate, formatUsd } from './searchFormat';

// The trader result table (trader spec 6.5 and 6.10, columns spec 4.1). The
// first grid column is the trader cell (full address, then the main category
// and the labels as chips); the rest come from TRADER_COLUMNS, of which each
// viewer sees a subset (state/traderColumnsPref.ts).

export type TraderColumnGroup = 'activity' | 'performance' | 'money';

export const TRADER_COLUMN_GROUPS: readonly {
    key: TraderColumnGroup;
    label: string;
}[] = [
    { key: 'activity', label: 'Activity' },
    { key: 'performance', label: 'Performance' },
    { key: 'money', label: 'Money' },
];

export interface TraderColumn {
    key: string;
    label: string;
    group: TraderColumnGroup;
    width: number;
    // 1 is the first column the automatic fit shows.
    priority: number;
    sort?: string;
    // The tooltip of a column without a sort key (spec appendix A.2).
    info?: string;
    // Names in TRADER_EXPORT_FIELDS that an "As shown" download writes.
    exportFields: readonly string[];
    value: (trader: TraderSearchResult, nowMs: number) => string;
    // A theme colour; text.primary when absent or undefined.
    color?: (trader: TraderSearchResult) => string | undefined;
}

// Wide enough for a full 42-character address at 12 px monospace. Fixed, so
// the columns start right after it (spec 4.1).
export const TRADER_CELL_WIDTH = 340;
export const TRADER_COLUMN_GAP = 8;
// The row's px 1.25 on both sides.
export const TRADER_ROW_PADDING = 20;

export const ROE_CAP = 10_000;
export const PROFIT_FACTOR_CAP = 100;

const DAY = 86_400_000;

function finite(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

// Each unit is taken only if the rounded figure still fits it, so 9,999 is
// "10k" (not "10.0k") and 999,999 is "1.0M" (not "1000k").
export function compactCount(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    const whole = Math.round(value);
    if (Math.abs(whole) < 1_000) return String(whole);
    const thousandsOne = (whole / 1_000).toFixed(1);
    if (Math.abs(Number(thousandsOne)) < 10) return `${thousandsOne}k`;
    const thousands = Math.round(whole / 1_000);
    if (Math.abs(thousands) < 1_000) return `${thousands}k`;
    return `${(whole / 1_000_000).toFixed(1)}M`;
}

// The stats service writes once a day, so whole UTC days are the right unit.
export function formatLastActive(
    iso: string | null | undefined,
    nowMs: number
): string {
    if (!iso) return EM_DASH;
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return EM_DASH;
    const days = Math.floor(nowMs / DAY) - Math.floor(ms / DAY);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 60) return `${days}d ago`;
    return formatDate(iso);
}

export function formatWinRate(trader: TraderSearchResult): string {
    const rate = trader.performance?.win_rate;
    if (rate == null) return EM_DASH;
    return `${Math.round(rate * 100)}% (${compactCount(trader.activity?.closed_count)})`;
}

// roi_pct is already a percentage (trader spec 4.7).
export function formatRoi(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    const text = value.toFixed(1);
    // A value that rounds to zero shows as 0.0%, never -0.0% or +0.0%.
    if (Number(text) === 0) return '0.0%';
    return `${value > 0 ? '+' : ''}${text}%`;
}

export function formatPerDay(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    return value < 100 ? value.toFixed(1) : String(Math.round(value));
}

// avg_hold_days is days from the first trade in a market to its end (spec A.1).
export function formatHold(days: number | null | undefined): string {
    if (!finite(days)) return EM_DASH;
    return days < 1 ? `${(days * 24).toFixed(1)}h` : `${days.toFixed(1)}d`;
}

// A 0-1 value as a whole percent.
export function formatShare(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    return `${Math.round(value * 100)}%`;
}

export function formatRatio(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    const text = value.toFixed(2);
    return Number(text) === 0 ? '0.00' : text;
}

// roe_pct is clamped to ±10,000 by the stats service (spec A.3).
export function formatRoe(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    if (value >= ROE_CAP) return '≥ +10,000%';
    if (value <= -ROE_CAP) return '≤ -10,000%';
    return formatRoi(value);
}

// 100 means no resolved market lost (spec A.3).
export function formatProfitFactor(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    if (value >= PROFIT_FACTOR_CAP) return '≥ 100';
    return formatRatio(value);
}

export function formatSignedUsd(value: number | null | undefined): string {
    if (!finite(value)) return EM_DASH;
    const text = formatUsd(Math.abs(value));
    if (text === '$0') return text;
    return `${value > 0 ? '+' : '-'}${text}`;
}

export function pnlColor(value: number | null | undefined): string {
    if (!finite(value) || value === 0) return 'text.secondary';
    return value > 0 ? 'success.main' : 'error.main';
}

function mutedAt(
    value: number | null | undefined,
    cap: number
): string | undefined {
    return finite(value) && Math.abs(value) >= cap
        ? 'text.secondary'
        : undefined;
}

// Display order; the priority only decides which columns the automatic fit
// shows (spec 4.1 and 4.2).
export const TRADER_COLUMNS: readonly TraderColumn[] = [
    {
        key: 'last_active',
        label: 'Last active',
        group: 'activity',
        width: 84,
        priority: 3,
        sort: 'last_seen',
        exportFields: ['last_seen'],
        value: (t, now) => formatLastActive(t.activity?.last_seen, now),
    },
    {
        key: 'first_seen',
        label: 'First seen',
        group: 'activity',
        width: 92,
        priority: 11,
        sort: 'first_seen',
        exportFields: ['first_seen'],
        value: t => formatDate(t.activity?.first_seen),
    },
    {
        key: 'trades',
        label: 'Trades',
        group: 'activity',
        width: 64,
        priority: 7,
        sort: 'total_trades',
        exportFields: ['total_trades'],
        value: t => compactCount(t.activity?.total_trades),
    },
    {
        key: 'trades_per_day',
        label: 'Trades/day',
        group: 'activity',
        width: 80,
        priority: 17,
        sort: 'trade_freq_per_day',
        exportFields: ['trade_freq_per_day'],
        value: t => formatPerDay(t.activity?.trade_freq_per_day),
    },
    {
        key: 'markets',
        label: 'Markets',
        group: 'activity',
        width: 68,
        priority: 12,
        sort: 'unique_markets',
        exportFields: ['unique_markets'],
        value: t => compactCount(t.activity?.unique_markets),
    },
    {
        key: 'avg_hold',
        label: 'Avg hold',
        group: 'activity',
        width: 72,
        priority: 18,
        sort: 'avg_hold_days',
        exportFields: ['avg_hold_days'],
        value: t => formatHold(t.activity?.avg_hold_days),
    },
    {
        key: 'volume',
        label: 'Volume',
        group: 'activity',
        width: 76,
        priority: 2,
        sort: 'lifetime_volume_usd',
        exportFields: ['lifetime_volume_usd'],
        value: t => formatUsd(t.activity?.lifetime_volume_usd),
    },
    {
        key: 'avg_size',
        label: 'Avg size',
        group: 'activity',
        width: 72,
        priority: 10,
        sort: 'avg_size_usd',
        exportFields: ['avg_size_usd'],
        value: t => formatUsd(t.activity?.avg_size_usd),
    },
    {
        key: 'bot',
        label: 'Bot',
        group: 'activity',
        width: 56,
        priority: 16,
        sort: 'algo_likelihood',
        exportFields: ['algo_likelihood'],
        value: t => formatShare(t.behavior?.algo_likelihood),
    },
    {
        key: 'pnl',
        label: 'PnL',
        group: 'performance',
        width: 80,
        priority: 1,
        sort: 'total_pnl',
        exportFields: ['total_pnl'],
        value: t => formatPnlShort(t.performance?.total_pnl),
        color: t => pnlColor(t.performance?.total_pnl),
    },
    {
        key: 'realized_pnl',
        label: 'Realized',
        group: 'performance',
        width: 80,
        priority: 14,
        sort: 'realized_pnl',
        exportFields: ['realized_pnl'],
        value: t => formatPnlShort(t.performance?.realized_pnl),
        color: t => pnlColor(t.performance?.realized_pnl),
    },
    {
        key: 'roi',
        label: 'ROI',
        group: 'performance',
        width: 68,
        priority: 5,
        sort: 'roi_pct',
        exportFields: ['roi_pct'],
        value: t => formatRoi(t.performance?.roi_pct),
    },
    {
        key: 'roe',
        label: 'ROE',
        group: 'performance',
        // Wide enough for the capped "≥ +10,000%".
        width: 88,
        priority: 20,
        info: 'ROE: total PnL as a share of the highest net deposits, in percent; capped at ±10,000%, which small net deposits reach easily.',
        exportFields: ['roe_pct'],
        value: t => formatRoe(t.performance?.roe_pct),
        color: t => mutedAt(t.performance?.roe_pct, ROE_CAP),
    },
    {
        key: 'win_rate',
        label: 'Win rate',
        group: 'performance',
        width: 92,
        priority: 4,
        sort: 'win_rate',
        exportFields: ['win_rate', 'closed_count'],
        value: t => formatWinRate(t),
    },
    {
        key: 'profit_factor',
        label: 'Profit factor',
        group: 'performance',
        width: 92,
        priority: 8,
        sort: 'profit_factor',
        exportFields: ['profit_factor'],
        value: t => formatProfitFactor(t.performance?.profit_factor),
        color: t => mutedAt(t.performance?.profit_factor, PROFIT_FACTOR_CAP),
    },
    {
        key: 'sharpe',
        label: 'Sharpe',
        group: 'performance',
        width: 64,
        priority: 13,
        sort: 'daily_sharpe',
        exportFields: ['daily_sharpe'],
        value: t => formatRatio(t.performance?.daily_sharpe),
    },
    {
        key: 'max_drawdown',
        label: 'Max drawdown',
        group: 'performance',
        width: 96,
        priority: 15,
        info: 'Max drawdown: largest fall of the cumulative PnL from its running peak (which starts at 0), checked once per recorded day.',
        exportFields: ['max_pnl_drawdown_usd'],
        value: t => formatUsd(t.performance?.max_pnl_drawdown_usd),
    },
    {
        key: 'portfolio',
        label: 'Portfolio',
        group: 'money',
        width: 76,
        priority: 6,
        sort: 'portfolio_value',
        exportFields: ['portfolio_value'],
        value: t => formatUsd(t.financials?.portfolio_value),
    },
    {
        key: 'balance',
        label: 'Balance',
        group: 'money',
        width: 76,
        priority: 9,
        sort: 'balance_usd',
        exportFields: ['balance_usd'],
        value: t => formatUsd(t.financials?.balance_usd),
    },
    {
        key: 'net_deposits',
        label: 'Net deposits',
        group: 'money',
        width: 92,
        priority: 19,
        info: 'Net deposits: deposits minus withdrawals. Both are derived from cash flows net of trade proceeds, so they can be off for accounts funded through trading.',
        exportFields: ['net_deposits'],
        value: t => formatSignedUsd(t.financials?.net_deposits),
    },
];

export function traderGridTemplate(columns: readonly TraderColumn[]): string {
    return [`${TRADER_CELL_WIDTH}px`, ...columns.map(c => `${c.width}px`)].join(
        ' '
    );
}

export function traderMinWidth(columns: readonly TraderColumn[]): number {
    return (
        TRADER_CELL_WIDTH +
        columns.reduce((sum, c) => sum + c.width + TRADER_COLUMN_GAP, 0) +
        TRADER_ROW_PADDING
    );
}

// The divider on the left of the first column of a group (spec 4.3). It is
// drawn in the middle of the 8 px column gap, so it takes no width from the
// column; `extend` stretches it over the cell's vertical padding.
export function groupDividerSx(extend = 0) {
    return {
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: -TRADER_COLUMN_GAP / 2,
            top: -extend,
            bottom: -extend,
            borderLeft: 1,
            borderColor: 'divider',
        },
    } as const;
}

// The first shown column of every group after the first: these get the
// group divider on their left.
export function groupStarts(columns: readonly TraderColumn[]): Set<string> {
    const starts = new Set<string>();
    let previous: TraderColumnGroup | null = null;
    for (const column of columns) {
        if (previous !== null && column.group !== previous)
            starts.add(column.key);
        previous = column.group;
    }
    return starts;
}
